import { yupResolver } from "@hookform/resolvers/yup";
import axios from "axios";
import Button from "components/Button";
import Loading from "components/Loading";
import Modal from "components/Modal";
import Title from "components/Title";
import CardLayout from "components/form/CardLayout";
import FormLayout from "components/form/FormLayout";
import TextInput from "components/inputs/TextInput";
import GalleryInput from "components/gallery/GalleryInput";
import GalleryModal from "components/gallery/GalleryModal";
import apiEndpoints from "configs/apiEndPoints";
import { FC, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import toastFunc from "utils/toast";
import { useMutationCustom, useQueryCustom } from "utils/useQueryCUstom";
import { CONFIGS } from "../configs";
import { fieldNames, validation } from "./validation";
import { OutputData } from "@editorjs/editorjs";
import SelectInputWithDrag from "components/inputs/SelectInputWithDrag";
import SlugInput2 from "components/inputs/SlugInput2";
import { getCurrentLanguage } from "utils/getCurrentLanguage";
import EditorJSEditor from "components/EditorJSEditor";

interface FormValues {
  _id?: string;
  title: string;
  shortDescription: string;
  slug: string;
  banner?: any;
  content: OutputData;
  listOrderNumber: number;
  language?: any;
  author: string;
  categoryIds: any[];
}

const langsList = [
  { id: 1, code: "en", name: "English" },
  { id: 2, code: "fa", name: "فارسی" },
  { id: 3, code: "ar", name: "العربية" },
  { id: 4, code: "tr", name: "Türkçe" },
];

const Add: FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const URL_ID = id || "";
  const { t } = useTranslation();
  const [editInitialLoading, setEditInitialLoading] = useState(!!id);

  // Banner
  const [showBannerModal, setShowBannerModal] = useState(false);
  const [bannerThumbnail, setBannerThumbnail] = useState<any | string | null>(
    null
  );

  // Gallery modal state for EditorJS
  const [showGalleryForEditor, setShowGalleryForEditor] = useState(false);
  const [galleryCallback, setGalleryCallback] =
    useState<(url: string) => void>();

  useEffect(() => {
    const handler = (e: any) => {
      setGalleryCallback(() => e.detail.callback);
      setShowGalleryForEditor(true);
    };
    window.addEventListener("open-gallery-for-editor", handler);
    return () => window.removeEventListener("open-gallery-for-editor", handler);
  }, []);

  const handleEditorGalleryImage = (photo: any) => {
    galleryCallback?.(photo.path || photo.url || photo);
    setShowGalleryForEditor(false);
  };

  const methods = useForm<FormValues>({
    resolver: yupResolver(validation(URL_ID, t)),
    defaultValues: {
      content: { blocks: [] } as OutputData,
    },
  });

  const updateOrCreate = URL_ID
    ? (params: any) =>
        axios.put(`${apiEndpoints[CONFIGS.apiName].base}/${URL_ID}`, params)
    : (params: any) => axios.post(apiEndpoints[CONFIGS.apiName].base, params);

  // --------------------------------------------------- language data
  const { data: languageData, isLoading: languageIsLoading } = useQueryCustom({
    name: `${CONFIGS.title}_language`,
    url: () => axios.get(apiEndpoints.languages.base),
    enabled: false,
  });

  // --------------------------------------------------- blog categories data
  const { data: blogCategoriesData, isLoading: blogCategoriesIsLoading } =
    useQueryCustom({
      name: `${CONFIGS.title}_blogCategories`,
      url: () => axios.get(apiEndpoints.blogCategories.base),
      enabled: true,
    });

  // --------------------------------------------------- data by id query
  const onSuccessDataById = ({ data }: any) => {
    const item = { ...data?.data };

    handleBanner(item?.banner);

    methods.reset({
      ...item,
      content: item?.content || { blocks: [] },
      categoryIds:
        item?.categoryIds?.map((id: string) => {
          const category = blogCategoriesData?.data?.data?.result?.find(
            (c: any) => c._id === id
          );
          return {
            value: id,
            label:
              category?.title?.[getCurrentLanguage()] ||
              category?.title?.en ||
              "No title",
          };
        }) || [],
      language: item?.language
        ? {
            value: item.language,
            label:
              langsList.find((l) => l.code === item.language)?.name ||
              item.language,
          }
        : undefined,
    });

    setEditInitialLoading(false);
  };

  useQueryCustom({
    name: `${CONFIGS.title}_getById`,
    url: () => axios.get(`${apiEndpoints[CONFIGS.apiName].base}/${URL_ID}`),
    onSuccess: onSuccessDataById,
    enabled: !!URL_ID,
  });

  // --------------------------------------------------- mutation
  const { isLoading, mutate: submitData } = useMutationCustom({
    url: updateOrCreate,
    invalidQuery: `${CONFIGS.title}_get`,
    onSuccess: () => {
      toastFunc.success({ title: "Success" });
      navigate(CONFIGS.routes.base);
    },
  });

  // --------------------------------------------------- onSubmit
  const onSubmit = (data: FormValues) => {
    const finalData = {
      title: data.title,
      shortDescription: data.shortDescription,
      slug: data.slug,
      banner: data?.banner?.path ? data.banner.path : bannerThumbnail,
      content: data.content,
      listOrderNumber: data.listOrderNumber,
      language: Array.isArray(data.language)
        ? data.language[0]?.value
        : typeof data.language === "object"
        ? (data.language as any).value
        : data.language,
      author: data.author,
      categoryIds: Array.isArray(data.categoryIds)
        ? data.categoryIds.map((c: any) => c.value || c)
        : [],
    };

    submitData(finalData);
  };

  // --------------------------------------------------- Banner Modal Handlers
  const handleBannerModal = () => setShowBannerModal(!showBannerModal);

  const handleBanner = (thumbnail: any | string) => {
    setBannerThumbnail(thumbnail);
    methods.setValue(fieldNames.banner, thumbnail);
    methods.clearErrors(fieldNames.banner);
  };

  // language options mapped for SelectInput
  const languageOptions =
    langsList.map((lang: any) => ({
      value: lang.code,
      label: lang.name,
    })) || [];
  const currentLanguage = getCurrentLanguage();

  // category options
  const categoryOptions =
    blogCategoriesData?.data?.data?.result?.map((c: any) => ({
      value: c._id,
      label: c.title?.[currentLanguage] || c.title?.en || "No title",
    })) || [];

  return (
    <div>
      {editInitialLoading && (
        <Modal isOpen title={t("loadingData")}>
          <div className="flex justify-center items-center">
            <Loading size="w-8 h-8" />
          </div>
        </Modal>
      )}

      <Title
        hasBtn
        btnTitle={t("backTitle", { changeablePart: t(CONFIGS.title) })}
        title={
          URL_ID
            ? t("editTitle", { changeablePart: t(CONFIGS.title) })
            : t("addNew", { changeablePart: t(CONFIGS.title) })
        }
        url={CONFIGS.routes.base + CONFIGS.routes.list}
      />

      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)} className="space-y-4">
          <FormLayout
            main={
              <>
                <CardLayout>
                  <SelectInputWithDrag
                    name={fieldNames.language}
                    label="language"
                    options={languageOptions}
                    placeholder={t("selectLanguage")}
                    isRequired
                    isLoading={languageIsLoading}
                  />

                  <TextInput
                    name={fieldNames.title}
                    label={fieldNames.title}
                    size="full"
                  />

                  <SlugInput2
                    name={fieldNames.slug}
                    label={fieldNames.slug}
                    size="full"
                    whatToWatch={fieldNames.title}
                  />

                  <TextInput
                    name={fieldNames.shortDescription}
                    label={fieldNames.shortDescription}
                    size="full"
                  />

                  <TextInput
                    name={fieldNames.author}
                    label={fieldNames.author}
                    size="full"
                  />

                  <SelectInputWithDrag
                    name={fieldNames.categoryIds}
                    label="categories"
                    options={categoryOptions}
                    isMulti
                    placeholder={t("selectCategories")}
                    isRequired
                    isLoading={blogCategoriesIsLoading}
                  />

                  <TextInput
                    type="number"
                    name={fieldNames.listOrderNumber}
                    label={fieldNames.listOrderNumber}
                    size="full"
                  />

                  <GalleryInput
                    isRequired
                    handleModal={handleBannerModal}
                    selectedPhoto={bannerThumbnail}
                    label={fieldNames.banner}
                    name={fieldNames.banner}
                    size="full"
                  />

                  <EditorJSEditor
                    name={fieldNames.content}
                    control={methods.control}
                    label={fieldNames.content}
                  />
                </CardLayout>
              </>
            }
            side={
              <CardLayout head={{ title: "action" }} className="sticky top-4">
                <Button
                  type="submit"
                  variant="success"
                  isFullSize
                  loading={isLoading}
                >
                  {t("submit")}
                </Button>
              </CardLayout>
            }
          />
        </form>
      </FormProvider>

      {/* Banner Modal */}
      <GalleryModal
        showModal={showBannerModal}
        handleModal={handleBannerModal}
        handleClickedImage={handleBanner}
        selectedPhoto={bannerThumbnail}
        FOLDER_SLUG="your-folder-slug"
      />

      {/* EditorJS Gallery Modal */}
      <GalleryModal
        showModal={showGalleryForEditor}
        handleModal={() => setShowGalleryForEditor(false)}
        handleClickedImage={handleEditorGalleryImage}
        selectedPhoto={null}
        FOLDER_SLUG="your-folder-slug"
      />
    </div>
  );
};

export default Add;
